import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  alpha,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

export type OverrideState = 'enabled' | 'disabled' | 'default';

export type FeatureToggleAction =
  | { type: 'set'; scope: 'global'; currentState: OverrideState }
  | { type: 'remove'; scope: 'global'; currentState: OverrideState }
  | {
      type: 'set';
      scope: 'org';
      orgName: string;
      currentState: OverrideState;
    }
  | {
      type: 'remove';
      scope: 'org';
      orgName: string;
      currentState: OverrideState;
      fallbackState: OverrideState;
      fallbackSource: string;
    };

export interface FeatureToggleConfirmDialogProps {
  open: boolean;
  featureKey: string;
  featureDescription: string;
  action: FeatureToggleAction;
  isLoading: boolean;
  onConfirm: (isEnabled: boolean) => void;
  onCancel: () => void;
}

const stateLabels: Record<OverrideState, string> = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  default: 'Off (default)',
};

function StateChip({ state }: { state: OverrideState }) {
  if (state === 'default') {
    return (
      <Chip
        label={stateLabels[state]}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 24,
          borderColor: 'text.disabled',
          color: 'text.secondary',
        }}
      />
    );
  }
  return (
    <Chip
      label={stateLabels[state]}
      color={state === 'enabled' ? 'success' : 'error'}
      size="small"
      variant="filled"
      sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
    />
  );
}

const FeatureToggleConfirmDialog: React.FC<FeatureToggleConfirmDialogProps> = ({
  open,
  featureKey,
  featureDescription,
  action,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [selectedState, setSelectedState] = useState<boolean>(true);

  const isSetAction = action.type === 'set';
  const skipConfirmInput = import.meta.env.DEV;
  const isConfirmTextValid = skipConfirmInput || confirmText === featureKey;
  const isGlobal = action.scope === 'global';

  // Default the toggle to the opposite of current effective state
  useEffect(() => {
    if (open && isSetAction) {
      setSelectedState(action.currentState !== 'enabled');
    }
  }, [open, isSetAction, action.currentState]);

  // New state chip for "remove" actions
  const removeNewState =
    action.type === 'remove' && 'fallbackState' in action
      ? action.fallbackState
      : 'default';
  const removeNewSource =
    action.type === 'remove' && 'fallbackSource' in action
      ? action.fallbackSource
      : undefined;

  const handleConfirm = () => {
    if (!isConfirmTextValid) return;
    onConfirm(isSetAction ? selectedState : false);
  };

  const handleClose = () => {
    if (isLoading) return;
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="feature-toggle-confirm-dialog"
      TransitionProps={{
        onExited: () => {
          setConfirmText('');
          setSelectedState(true);
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header with scope accent */}
      <Box
        sx={theme => ({
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(135deg, ${alpha(
            isGlobal ? theme.palette.warning.main : theme.palette.info.main,
            0.08
          )} 0%, transparent 60%)`,
        })}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 1.5,
          }}
        >
          <Box
            sx={theme => ({
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(
                isGlobal ? theme.palette.warning.main : theme.palette.info.main,
                0.15
              ),
              color: isGlobal ? 'warning.main' : 'info.main',
            })}
          >
            {isGlobal ? (
              <PublicIcon fontSize="small" />
            ) : (
              <BusinessIcon fontSize="small" />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {isSetAction ? 'Set Override' : 'Remove Override'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isGlobal
                ? 'Affects all organisations'
                : `${'orgName' in action ? action.orgName : ''} only`}
            </Typography>
          </Box>
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* Feature identity */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              fontSize: '0.85rem',
              letterSpacing: '-0.01em',
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            {featureKey}
          </Typography>
          {featureDescription && (
            <Typography variant="caption" color="text.secondary">
              {featureDescription}
            </Typography>
          )}
        </Box>

        {/* State transition */}
        <Box
          sx={theme => ({
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            mb: 2.5,
            bgcolor: alpha(theme.palette.background.default, 0.5),
          })}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing={0.5}
            sx={{ display: 'block', mb: 1.5 }}
          >
            State Change
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <StateChip state={action.currentState} />
            <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            {isSetAction ? (
              <StateChip state={selectedState ? 'enabled' : 'disabled'} />
            ) : (
              <StateChip state={removeNewState} />
            )}
          </Box>
          {!isSetAction && removeNewSource && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}
            >
              Falls back to {removeNewSource}
            </Typography>
          )}
        </Box>

        {/* Override state selector */}
        {isSetAction && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={0.5}
              sx={{ display: 'block', mb: 1 }}
            >
              Override State
            </Typography>
            <ToggleButtonGroup
              value={selectedState}
              exclusive
              onChange={(_, value) => {
                if (value !== null) setSelectedState(value);
              }}
              size="small"
              fullWidth
              data-testid="override-state-toggle"
              sx={{
                '& .MuiToggleButton-root': {
                  py: 1,
                  gap: 0.75,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:not(:first-of-type)': {
                    ml: 1,
                    borderLeft: '1px solid',
                    borderLeftColor: 'divider',
                  },
                },
              }}
            >
              <ToggleButton value={true} color="success">
                <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                Enabled
              </ToggleButton>
              <ToggleButton value={false} color="error">
                <HighlightOffIcon sx={{ fontSize: 18 }} />
                Disabled
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Type-to-confirm */}
        {!skipConfirmInput && (
          <Box
            sx={theme => ({
              border: '1px solid',
              borderColor: isConfirmTextValid
                ? alpha(theme.palette.success.main, 0.4)
                : 'divider',
              borderRadius: 2,
              p: 2,
              transition: 'border-color 0.2s ease',
            })}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Type{' '}
              <Box
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.8rem',
                }}
              >
                {featureKey}
              </Box>{' '}
              to confirm
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder={featureKey}
              autoComplete="off"
              data-testid="confirm-text-input"
              slotProps={{
                input: {
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                  },
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          sx={{ fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!isConfirmTextValid || isLoading}
          data-testid="feature-toggle-confirm-button"
          startIcon={
            !isLoading ? (
              isSetAction ? (
                selectedState ? (
                  <CheckCircleOutlineIcon />
                ) : (
                  <HighlightOffIcon />
                )
              ) : (
                <RemoveCircleOutlineIcon />
              )
            ) : undefined
          }
          sx={{ fontWeight: 600, px: 3 }}
        >
          {isLoading
            ? 'Applying...'
            : isSetAction
              ? selectedState
                ? 'Enable'
                : 'Disable'
              : 'Remove Override'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeatureToggleConfirmDialog;
